import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../../../../store";
import {
  BenchmarkStatus,
  ColorCriteria,
  EvidenceColumnGroup,
  EvidencePeriodData,
  School,
  Student,
} from "../../../../../../store/onboarding/types";
import _ from "lodash";
import { getStudentGradeFromEcg } from "../../../../../common/onboarding/second-step/evidence-tab/student-data-table/Helpers";
import { Grade, TimePeriod } from "../../../../../../store/groups/types";
import useUserRole from "../../../../../../utils/hooks/useUserRole";
import { getSchools } from "../../../../../../store/onboarding/actions";

export type SchoolOption = {
  label?: string;
  value: number;
};

export type BenchmarkStats = {
  belowColor?: string;
  atOrAboveColor?: string;
  below: number[];
  atOrAbove: number[];
};

// A student record after we've filled in its grade (falling back to the
// grade recorded on the evidence column groups if the student itself
// doesn't have one set directly).
export type EnrichedStudent = Omit<Student, "grade"> & { grade?: Grade | string };

/**
 * Takes the raw student rows from the evidence period data and returns a
 * flat list of students, each with `grade` filled in using the same
 * fallback order the table has always used:
 *   1. student.grade
 *   2. the grade recorded on the student row itself
 *   3. the grade derived from the evidence column groups
 */
export const getStudentsWithResolvedGrade = (
  fullEvidencePeriodData: EvidencePeriodData
): EnrichedStudent[] => {
  return fullEvidencePeriodData.student_rows.map((studentRow) => ({
    ...studentRow.student,
    grade:
      studentRow.grade 
  }));
};

export const studentAttendsSelectedSchool = (
  student: EnrichedStudent,
  selectedSchool: number | undefined
): boolean => {
  return !!student.teachers?.some((teacher) =>
    teacher.schools?.includes(selectedSchool!)
  );
};

/**
 * Builds a dictionary of "group value" -> "student ids in that group" for a
 * single demographic (race, special education, ELL, SLD, gender, free/reduced
 * lunch, etc). This is a direct, readable replacement for the 6 blocks of
 * near-identical `_.chain(...)` code that used to live in this file - one per
 * demographic - which only differed in their `filter`/`groupBy` logic.
 *
 * @param includeStudent   decides whether a student belongs in this
 *                         demographic grouping at all (e.g. only students
 *                         flagged `special_education === true`)
 * @param getGroupValue    decides which bucket (group key) an included
 *                         student falls into (e.g. their race, or their
 *                         gender)
 */
export const buildStudentDictionary = (
  fullEvidencePeriodData: EvidencePeriodData | undefined,
  selectedGradeKey: Grade,
  selectedSchool: number | undefined,
  includeStudent: (student: EnrichedStudent) => boolean,
  getGroupValue: (student: EnrichedStudent) => string | number | boolean | undefined
): Record<string, number[]> => {
  if (!fullEvidencePeriodData) {
    return {};
  }

  const studentsInSelectedGrade = getStudentsWithResolvedGrade(
    fullEvidencePeriodData
  ).filter(
    (student) =>
      student.grade === selectedGradeKey &&
      studentAttendsSelectedSchool(student, selectedSchool) &&
      includeStudent(student)
  );

  const studentsGroupedByValue = _.groupBy(studentsInSelectedGrade, getGroupValue);

  return _.mapValues(studentsGroupedByValue, (students) =>
    students.map((student) => student.id!)
  );
};

export const useEquityBenchmarkData = (
  selectedGradeKey: Grade,
  selectedSchool: number | undefined
) => {
  const { isDistrictOwner } = useUserRole();
  const dispatch = useDispatch();

  const selectedEvidenceColumnGroupId = useSelector<
    ApplicationState,
    number | undefined
  >((s) => s.onboarding.selectedEvidenceColumnGroupId);

  const fullEvidencePeriodData = useSelector<
    ApplicationState,
    EvidencePeriodData | undefined
  >((s) => s.onboarding.fullEvidenceInDataPeriod);

  const dataPeriodSchools = useSelector<ApplicationState, School[]>(
    (s) => s.onboarding.dataPeriodSchools
  );

  const schools = useSelector<ApplicationState, School[]>(
    (s) => s.onboarding.schools
  );

  useEffect(() => {
    if (isDistrictOwner && !schools.length) {
      dispatch(getSchools());
    }
  }, [schools]);

  // Builds the list of schools shown in the "School" filter dropdown by
  // walking every student's teachers and collecting each unique school id
  // referenced anywhere in the currently loaded evidence period data.
  const schoolOptions: SchoolOption[] = useMemo(() => {
    const schoolArray: SchoolOption[] = [];
    for (let i = 0; i < fullEvidencePeriodData?.student_rows.length!; i++) {
      const element = fullEvidencePeriodData?.student_rows[i];
      for (let j = 0; j < element?.student?.teachers?.length!; j++) {
        const teacher = element?.student?.teachers![j];
        for (let k = 0; k < teacher?.schools?.length!; k++) {
          const schoolId = teacher?.schools![k];
          if (!schoolArray.some((sc) => sc.value == schoolId)) {
            schoolArray.push({
              label: (
                schools.find((scLabel) => scLabel.id == schoolId) ||
                dataPeriodSchools.find((scLabel) => scLabel.id == schoolId)
              )?.name,
              value: schoolId!,
            });
          }
        }
      }
    }
    return schoolArray;
  }, [fullEvidencePeriodData, schools, dataPeriodSchools]);

  const evidenceColumnGroup: EvidenceColumnGroup | undefined = useMemo(() => {
    return selectedEvidenceColumnGroupId
      ? fullEvidencePeriodData?.evidence_column_groups.find(
          (ecg) => ecg.id === selectedEvidenceColumnGroupId
        )
      : undefined;
  }, [selectedEvidenceColumnGroupId, fullEvidencePeriodData, selectedGradeKey]);

  // Each demographic dictionary below answers "for the selected grade and
  // school, which students fall into each group of this demographic?".
  // They all share the same building block (`buildStudentDictionary`) and
  // only differ in which students are included and how they're grouped.

  const raceDictionary = useMemo(
    () =>
      buildStudentDictionary(
        fullEvidencePeriodData,
        selectedGradeKey,
        selectedSchool,
        () => true, // every student in the selected grade/school counts
        (student) => student.race
      ),
    [fullEvidencePeriodData, selectedGradeKey, selectedSchool]
  );

  const specialEducationDictionary = useMemo(
    () =>
      buildStudentDictionary(
        fullEvidencePeriodData,
        selectedGradeKey,
        selectedSchool,
        (student) => student.special_education === true,
        (student) => student.special_education
      ),
    [fullEvidencePeriodData, selectedGradeKey, selectedSchool]
  );

  const ELLDictionary = useMemo(
    () =>
      buildStudentDictionary(
        fullEvidencePeriodData,
        selectedGradeKey,
        selectedSchool,
        (student) => student.ell === true,
        (student) => student.ell
      ),
    [fullEvidencePeriodData, selectedGradeKey, selectedSchool]
  );

  const SLDDictionary = useMemo(
    () =>
      buildStudentDictionary(
        fullEvidencePeriodData,
        selectedGradeKey,
        selectedSchool,
        (student) => student.sld === true,
        (student) => student.sld
      ),
    [fullEvidencePeriodData, selectedGradeKey, selectedSchool]
  );

  const genderDictionary = useMemo(
    () =>
      buildStudentDictionary(
        fullEvidencePeriodData,
        selectedGradeKey,
        selectedSchool,
        () => true, // every student in the selected grade/school counts
        (student) => student.gender
      ),
    [fullEvidencePeriodData, selectedGradeKey, selectedSchool]
  );

  const freeLunchDictionary = useMemo(
    () =>
      buildStudentDictionary(
        fullEvidencePeriodData,
        selectedGradeKey,
        selectedSchool,
        (student) => student.free_or_reduced_lunch === true,
        (student) => student.free_or_reduced_lunch
      ),
    [fullEvidencePeriodData, selectedGradeKey, selectedSchool]
  );

  const currentTimePeriod = useSelector<ApplicationState, TimePeriod | undefined>(
    (s) => s.onboarding.currentDataPeriod?.time_period
  );

  const stats: Record<number, BenchmarkStats | null> = useMemo(
    () =>
      buildStatsByEvidenceColumn(
        evidenceColumnGroup,
        selectedGradeKey,
        currentTimePeriod
      ),
    [evidenceColumnGroup, selectedGradeKey, selectedSchool, currentTimePeriod]
  );

  const allStudents = useMemo(
    () => Object.values(raceDictionary).flat(),
    [raceDictionary]
  );

  // The columns actually rendered in the table - "Percentile" columns are
  // never displayed. Computed once here instead of being recalculated with
  // an inline `.filter(...)` call every time it was needed in the JSX.
  const displayedEvidenceColumns = useMemo(
    () =>
      evidenceColumnGroup?.evidence_columns.filter(
        (ec) => !ec.measurement.display_name?.includes("Percentile")
      ) ?? [],
    [evidenceColumnGroup]
  );

  return {
    schoolOptions,
    evidenceColumnGroup,
    raceDictionary,
    specialEducationDictionary,
    ELLDictionary,
    SLDDictionary,
    genderDictionary,
    freeLunchDictionary,
    stats,
    allStudents,
    displayedEvidenceColumns,
  };
};

/**
 * For every evidence column in the group, works out:
 *  - which students fall "below" vs "at/above" benchmark for the selected
 *    grade/time period, based on each column's color criteria ranges
 *  - the legend colors to use for "below" and "at/above"
 *
 * Returns `null` for a column when there's no color criteria configured for
 * the selected grade/time period (nothing to display).
 */
export const buildStatsByEvidenceColumn = (
  evidenceColumnGroup: EvidenceColumnGroup | undefined,
  selectedGradeKey: Grade,
  currentTimePeriod: TimePeriod | undefined
): Record<number, BenchmarkStats | null> => {
  if (!evidenceColumnGroup || !currentTimePeriod) {
    return {};
  }

  const statsByColumnId: Record<number, BenchmarkStats | null> = {};

  for (const evidenceColumn of evidenceColumnGroup.evidence_columns) {
    const colorCriteriaForGrade =
      evidenceColumn.measurement != null
        ? evidenceColumn.measurement.grade_specific_color_criteria?.[
            selectedGradeKey
          ]?.[currentTimePeriod]
        : [];

    statsByColumnId[evidenceColumn.id] = colorCriteriaForGrade?.length
      ? buildStatsForColumn(evidenceColumn.student_entries, colorCriteriaForGrade)
      : null;
  }

  return statsByColumnId;
};

export const buildStatsForColumn = (
  studentEntries: EvidenceColumnGroup["evidence_columns"][number]["student_entries"],
  colorCriteriaForGrade: (ColorCriteria | null)[]
): BenchmarkStats => {
  const validColorCriteria = colorCriteriaForGrade.filter((cc) => cc != null) as ColorCriteria[];

  const belowColor = validColorCriteria.find(
    (cc) =>
      cc.benchmark_status === BenchmarkStatus.Below ||
      cc.benchmark_status === BenchmarkStatus.WellBelow
  )?.color;

  const atOrAboveColor = validColorCriteria.find(
    (cc) => cc.benchmark_status === BenchmarkStatus.AtOrAbove
  )?.color;

  const below: number[] = [];
  const atOrAbove: number[] = [];

  for (const entry of studentEntries) {
    const value = entry.value !== undefined ? +entry.value : undefined;
    if (!value && value !== 0) {
      continue;
    }

    const matchingCriteria = validColorCriteria.find(
      (cc) => value >= +cc.range_min! && value <= +cc.range_max!
    );
    if (!matchingCriteria || !matchingCriteria.benchmark_status) {
      continue;
    }

    if (matchingCriteria.benchmark_status === BenchmarkStatus.AtOrAbove) {
      atOrAbove.push(entry.student_id);
    } else {
      below.push(entry.student_id);
    }
  }

  return { belowColor, atOrAboveColor, below, atOrAbove };
};

export default useEquityBenchmarkData;
